import React from 'react';

/**
 * UnifiedInput — text / number / tel / email / password input.
 * Has integrated label, helper text, error state, and adapts to theme.
 *
 * Props:
 *  - label, helperText, error
 *  - leftIcon, rightIcon
 *  - required (adds gold asterisk)
 *  - full <input> props passthrough
 */
export const UnifiedInput = React.forwardRef(function UnifiedInput(
  {
    label,
    helperText,
    error,
    leftIcon,
    rightIcon,
    required = false,
    className = '',
    inputClassName = '',
    style,
    testId,
    ...inputProps
  },
  ref
) {
  const hasError = !!error;

  return (
    <div className={`mesp-ui ${className}`} style={style}>
      {label && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--mesp-font-sm)',
            fontWeight: 'var(--mesp-font-weight-medium)',
            color: 'var(--mesp-text)',
            marginBottom: 'var(--mesp-space-2)',
          }}
          htmlFor={inputProps.id}
        >
          {label}
          {required && <span style={{ color: 'var(--mesp-gold)' }} aria-hidden>*</span>}
        </label>
      )}

      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          background: 'var(--mesp-bg-sunken)',
          border: `1px solid ${hasError ? 'var(--mesp-danger)' : 'var(--mesp-border)'}`,
          borderRadius: 'var(--mesp-radius-md)',
          transition: 'border-color var(--mesp-trans-fast), background var(--mesp-trans-fast)',
        }}
        onFocus={(e) => {
          if (!hasError) e.currentTarget.style.borderColor = 'var(--mesp-accent)';
        }}
        onBlur={(e) => {
          if (!hasError) e.currentTarget.style.borderColor = 'var(--mesp-border)';
        }}
      >
        {leftIcon && (
          <div
            style={{
              paddingInlineStart: 'var(--mesp-space-3)',
              color: 'var(--mesp-text-muted)',
              display: 'flex',
            }}
          >
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          data-testid={testId}
          className={inputClassName}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '0.625rem 0.875rem',
            color: 'var(--mesp-text)',
            fontSize: 'var(--mesp-font-body)',
            fontFamily: 'inherit',
            minHeight: 40,
            width: '100%',
          }}
          {...inputProps}
        />
        {rightIcon && (
          <div
            style={{
              paddingInlineEnd: 'var(--mesp-space-3)',
              color: 'var(--mesp-text-muted)',
              display: 'flex',
            }}
          >
            {rightIcon}
          </div>
        )}
      </div>

      {(helperText || error) && (
        <p
          style={{
            marginTop: 'var(--mesp-space-1)',
            fontSize: 'var(--mesp-font-xs)',
            color: hasError ? 'var(--mesp-danger)' : 'var(--mesp-text-dim)',
            margin: '4px 0 0 0',
          }}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
});

export const UnifiedTextarea = React.forwardRef(function UnifiedTextarea(
  { label, helperText, error, required = false, className = '', testId, rows = 4, style, ...rest },
  ref
) {
  const hasError = !!error;
  return (
    <div className={`mesp-ui ${className}`} style={style}>
      {label && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--mesp-font-sm)',
            fontWeight: 'var(--mesp-font-weight-medium)',
            color: 'var(--mesp-text)',
            marginBottom: 'var(--mesp-space-2)',
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--mesp-gold)' }} aria-hidden>*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        rows={rows}
        data-testid={testId}
        style={{
          width: '100%',
          background: 'var(--mesp-bg-sunken)',
          border: `1px solid ${hasError ? 'var(--mesp-danger)' : 'var(--mesp-border)'}`,
          borderRadius: 'var(--mesp-radius-md)',
          padding: '0.625rem 0.875rem',
          color: 'var(--mesp-text)',
          fontSize: 'var(--mesp-font-body)',
          fontFamily: 'inherit',
          outline: 'none',
          resize: 'vertical',
          transition: 'border-color var(--mesp-trans-fast)',
        }}
        onFocus={(e) => { if (!hasError) e.currentTarget.style.borderColor = 'var(--mesp-accent)'; }}
        onBlur={(e) => { if (!hasError) e.currentTarget.style.borderColor = 'var(--mesp-border)'; }}
        {...rest}
      />
      {(helperText || error) && (
        <p style={{ marginTop: 4, fontSize: 'var(--mesp-font-xs)', color: hasError ? 'var(--mesp-danger)' : 'var(--mesp-text-dim)' }}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

export const UnifiedSelect = React.forwardRef(function UnifiedSelect(
  { label, helperText, error, required = false, className = '', testId, style, children, ...rest },
  ref
) {
  const hasError = !!error;
  return (
    <div className={`mesp-ui ${className}`} style={style}>
      {label && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--mesp-font-sm)',
            fontWeight: 'var(--mesp-font-weight-medium)',
            color: 'var(--mesp-text)',
            marginBottom: 'var(--mesp-space-2)',
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--mesp-gold)' }} aria-hidden>*</span>}
        </label>
      )}
      <select
        ref={ref}
        data-testid={testId}
        style={{
          width: '100%',
          background: 'var(--mesp-bg-sunken)',
          border: `1px solid ${hasError ? 'var(--mesp-danger)' : 'var(--mesp-border)'}`,
          borderRadius: 'var(--mesp-radius-md)',
          padding: '0.625rem 0.875rem',
          color: 'var(--mesp-text)',
          fontSize: 'var(--mesp-font-body)',
          fontFamily: 'inherit',
          minHeight: 40,
          outline: 'none',
          transition: 'border-color var(--mesp-trans-fast)',
          appearance: 'none',
        }}
        {...rest}
      >
        {children}
      </select>
      {(helperText || error) && (
        <p style={{ marginTop: 4, fontSize: 'var(--mesp-font-xs)', color: hasError ? 'var(--mesp-danger)' : 'var(--mesp-text-dim)' }}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

export default UnifiedInput;
